<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed, inject, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import { useDebounceFn } from "@vueuse/core"
import { createUniqueId } from "@core/helpers"
import { useAuthStore } from "@core/stores/auth.store"
import { useTaskStore } from "@core/stores/task.store"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import type { Services } from "@services"
import {
    compositionCoherente,
    offreServie,
    texteEcart,
    texteFacturees,
    textePeriode,
    TEXTE_FACTUREES_INDISPONIBLE,
    useCompanyPlafond,
} from "@core/composables/useCompanyPlafond"
import CompanyBillingSourceList from "@modules/espace-entreprise/components/company/billing/CompanyBillingSourceList.vue"
import CompanySubscriptionDecompteBarre from "@modules/espace-entreprise/components/company/subscriptions/CompanySubscriptionDecompteBarre.vue"
import {
    DETAIL_TOUTES_LES_CARTES,
    memoriserDetailOuvert,
} from "@modules/espace-entreprise/components/company/subscriptions/detailDecompte"

/**
 * Page « Sources facturées » (DEV-621, demande de Mathieu du 16/10).
 *
 * INTENTION. La liste des sources n'est plus un modal : c'est une page, à
 * laquelle mènent « Voir les sources », chaque nombre du tableau du décompte,
 * chaque libellé de la barre de synthèse et le lien des courriels d'alerte.
 * En tête, la période et les sources facturées en une ligne, puis la barre
 * de synthèse du forfait ; dessous, la liste filtrable. Les filtres vivent
 * dans la query de la route (`?sur_le_compte=oui&si_supprimee=libere`) : un
 * lien filtré se partage et le retour arrière les retrouve.
 */

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Sources facturées",
            retour: "Retour à l'espace entreprise",
            criteres: "Comprendre les critères : voir le tableau",
            liste: "Liste",
            period_label: "Période de facturation de l'abonnement.",
            breadcrumb: {
                accueil: "Accueil",
                utilisateur: "Utilisateur",
                entreprise: "Espace entreprise",
                sources: "Sources facturées",
            },
            export_button: "Exporter les données",
            export_label: "Export des sources de facturation",
            download_blocked:
                "Votre navigateur a bloqué l'ouverture du fichier. Cliquez ci-dessous pour le récupérer.",
            download_link: "Télécharger le fichier",
            export_failed:
                "L'export n'a pas pu être généré. Merci de réessayer dans quelques instants.",
        },
    },
})

// ================================
// SERVICES & STORES
// ================================
const services = inject<Services>("services")!
const router = useRouter()
const authStore = useAuthStore()
const taskStore = useTaskStore()

/** Décompte servi : bloc `plafond` et nombre facturé */
const {
    plafond,
    total: totalServi,
    isLoaded: isDecompteLoaded,
    fetch: fetchPlafond,
} = useCompanyPlafond()

// ================================
// COMPUTED
// ================================

/** Fil d'Ariane : Accueil › Utilisateur › Espace entreprise › Sources facturées */
const getBreadcrumb = computed(() => [
    { icon: "pi pi-home", label: t("breadcrumb.accueil"), route: "/" },
    { icon: "pi pi-user", label: t("breadcrumb.utilisateur") },
    {
        icon: "pi pi-building",
        label: t("breadcrumb.entreprise"),
        route: "/user/entreprise",
    },
    { icon: "pi pi-list", label: t("breadcrumb.sources") },
])

/** Composition servie et cohérente avec le total servi, sinon `null` */
const getComposition = computed(() => compositionCoherente(plafond.value))

/** Offre dont la règle s'applique, `null` si aucune n'est connue */
const getOffre = computed(() =>
    offreServie(plafond.value, authStore.companyStripeSubscriptions)
)

/**
 * « 40 sources facturées : 39 sur votre compte, 1 supprimée pendant la
 * période, facturée jusqu'au 12/11. » ; sans nombre servi, la mention
 * d'indisponibilité ; avant la réponse, rien.
 */
const getFactureesText = computed((): string | null => {
    if (!isDecompteLoaded.value) return null
    const n = plafond.value?.facturees ?? totalServi.value
    if (n === null || n === undefined) return TEXTE_FACTUREES_INDISPONIBLE
    return texteFacturees(
        n,
        getComposition.value,
        plafond.value?.periode_fin ?? null
    )
})

/**
 * Ligne de tête : « Période du 12/10 au 12/11. 40 sources facturées : ….
 * 60 places libres. », lue du seul bloc servi ; sans période servie,
 * « Période de facturation de l'abonnement. » (jamais de dates recalculées)
 */
const getEnTete = computed((): string => {
    const p = plafond.value
    return [
        textePeriode(p?.periode_debut ?? null, p?.periode_fin ?? null) ??
            t("period_label"),
        getFactureesText.value,
        p && getFactureesText.value ? texteEcart(p.forfait, p.facturees) : null,
    ]
        .filter((x): x is string => !!x)
        .join(" ")
})

// ================================
// EXPORT
// ================================

/** URL du fichier quand le navigateur a bloqué son ouverture automatique */
const blockedDownloadUrl = ref("")

const {
    fetch: fetchExport,
    isFetching: isExporting,
    hasError: hasExportError,
    error: exportError,
} = useFetchWrapper(
    async () => {
        blockedDownloadUrl.value = ""
        const response = await services.$downloadExportSourceBilling()
        if (response.status !== "success" || !response.result?.url) {
            // Sans URL il n'y a rien à ouvrir : le dire, au lieu de laisser
            // croire que l'export est parti.
            throw new Error(t("export_failed"))
        }
        // L'ouverture n'est plus rattachée au clic après l'attente : sur un gros
        // compte le navigateur la bloque, on laisse alors un lien cliquable.
        if (!window.open(response.result.url, "_blank")) {
            blockedDownloadUrl.value = response.result.url
        }
        taskStore.followTask(createUniqueId(), {
            taskname: t("export_label"),
            notification: true,
            autoDelete: true,
        })
    },
    (err) => {
        console.error("Error exporting billing sources:", err)
    }
)

/** Déclenche l'export (débounced) */
const onExportClick = useDebounceFn(() => {
    fetchExport()
}, 1000)

// ================================
// INTERACTIONS
// ================================

/** Retour à la section Abonnements de l'espace entreprise */
function onRetour() {
    void router?.push({ name: "PageCompany", hash: "#subscriptions" })
}

/**
 * Vers la carte d'abonnement et son tableau des critères : l'ancre de la
 * section Abonnements (le tableau n'a pas d'ancre propre ; la page
 * Entreprise cherche l'ancre 100 ms après son montage, avant que le
 * décompte servi n'ait rendu le tableau). Le tableau, replié par défaut sur
 * la carte (DEV-2480), s'y montre déplié sur toutes les cartes : c'est ce
 * qui est demandé ; un choix explicite par carte prime ensuite.
 */
function onCriteres() {
    memoriserDetailOuvert(DETAIL_TOUTES_LES_CARTES, true)
    void router?.push({ name: "PageCompany", hash: "#subscriptions" })
}

// ================================
// LIFECYCLE
// ================================
onMounted(() => {
    void fetchPlafond()
})
</script>

<template>
    <div class="page page-company-sources">
        <!-- ================================ -->
        <!-- RETOUR ET EN-TÊTE -->
        <!-- ================================ -->
        <div class="flex flex-col gap-4">
            <BaseButton
                variant="link"
                size="small"
                icon="pi pi-arrow-left"
                class="page-company-sources__retour w-fit p-0!"
                :label="t('retour')"
                @click="onRetour"
            />
            <HeaderPage
                :title="t('title')"
                :breadcrumb="getBreadcrumb"
                class-title="text-surface-800"
            >
                <template #subtitle>
                    <p
                        class="page-company-sources__tete text-sm text-surface-600"
                    >
                        {{ getEnTete }}
                    </p>
                </template>
            </HeaderPage>
        </div>

        <!-- ================================ -->
        <!-- BARRE DE SYNTHÈSE DU FORFAIT -->
        <!-- ================================ -->
        <BaseSkeleton
            v-if="!isDecompteLoaded"
            width="100%"
            height="3rem"
            class="page-company-sources__chargement"
        />
        <div
            v-else-if="plafond && getComposition && getOffre"
            class="page-company-sources__synthese flex flex-col gap-2"
        >
            <CompanySubscriptionDecompteBarre
                :composition="getComposition"
                :facturees="plafond.facturees"
                :forfait="plafond.forfait"
                :offre="getOffre"
                :fin="plafond.periode_fin"
            />
            <BaseButton
                variant="link"
                size="small"
                class="page-company-sources__criteres w-fit p-0!"
                :label="t('criteres')"
                @click="onCriteres"
            />
        </div>

        <!-- ================================ -->
        <!-- LISTE -->
        <!-- ================================ -->
        <BaseTitledCard class="page-company-sources__liste">
            <template #header>
                {{ t("liste") }}
            </template>
            <template #content>
                <div class="flex flex-col gap-3">
                    <BaseMessage
                        v-if="hasExportError"
                        severity="error"
                        size="small"
                        variant="simple"
                        class="page-company-sources__erreur"
                    >
                        {{ exportError?.message }}
                    </BaseMessage>
                    <BaseMessage v-if="blockedDownloadUrl" severity="warn">
                        {{ t("download_blocked") }}
                        <a
                            :href="blockedDownloadUrl"
                            target="_blank"
                            rel="noopener"
                            class="underline font-medium"
                        >
                            {{ t("download_link") }}
                        </a>
                    </BaseMessage>
                    <CompanyBillingSourceList
                        :periode-fin="plafond?.periode_fin ?? null"
                        :plafond="plafond"
                        :offre="getOffre"
                    >
                        <template #actions>
                            <BaseButton
                                :label="t('export_button')"
                                severity="secondary"
                                outlined
                                icon="pi pi-download"
                                class="shrink-0"
                                :loading="isExporting"
                                :disabled="isExporting"
                                @click="onExportClick"
                            />
                        </template>
                    </CompanyBillingSourceList>
                </div>
            </template>
        </BaseTitledCard>
    </div>
</template>
